import React, { useState } from 'react';

import pluralize from 'pluralize';

import { Box, Button, Stack, Typography } from '@mui/material';

import { LUX_LOYALTY_PROGRAM_CONFIG, LUX_LOYALTY_TIERS_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import CopyableField from '~/components/Common/CopyableField';

import { ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import { formatDateSlashes, formatDateSlashesWithClock } from '~/services/TimeService';

import isProd from '~/utils/isProd';
import { formatDecimal } from '~/utils/numberFormatters';
import { titleCase } from '~/utils/stringUtils';

import LuxLoyaltyAdjustPointsModal from './LuxLoyaltyAdjustPointsModal';
import LuxLoyaltyChangeTierModal from './LuxLoyaltyChangeTierModal';
import RegionSwitcher from './RegionSwitcher';

const UPGRADEABLE_TIERS: Array<App.LuxLoyaltyTier> = ['bronze', 'silver', 'gold'];

interface Props {
  account: App.LuxLoyaltyAccount;
  onAccountUpdated?: () => void;
}

export default function LuxLoyaltyAccountOverview({ account, onAccountUpdated }: Props) {
  const formattedStatusCreditsName = titleCase(pluralize(LUX_LOYALTY_PROGRAM_CONFIG.statusCreditsUnit));
  const formattedCurrencyName = titleCase(pluralize(LUX_LOYALTY_PROGRAM_CONFIG.pointsUnit));
  const { user } = useCurrentUser();
  const [changeTierModalOpen, setChangeTierModalOpen] = useState(false);
  const canChangeTier = !isProd() || !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);
  const [adjustPointsModalOpen, setAdjustPointsModalOpen] = useState(false);
  const canAdjustPoints = !isProd() || !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);

  return (
    <Stack direction="column" gap={4} flex={1}>
      {account.accountNumber ? (
        <Box>
          <Typography variant="h6">{LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} LE Member ID</Typography>
          <CopyableField variant="subtitle1" value={account.accountNumber} />
        </Box>
      ) : null}
      <Box>
        <Typography variant="h6">{LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} External Provider ID</Typography>
        <CopyableField variant="subtitle1" value={account.ibsMemberId} />
      </Box>
      <Box>
        <Typography variant="h6">Region</Typography>
        <Typography variant="subtitle1">{account.region}</Typography>
      </Box>
      <RegionSwitcher account={account} onRegionUpdated={onAccountUpdated} />
      {!!account.createdAt && (
        <Box>
          <Typography variant="h6">Account Created</Typography>
          <Typography variant="subtitle1">{formatDateSlashesWithClock(account.createdAt)}</Typography>
        </Box>
      )}
      <Box>
        <Typography variant="h6">Tier</Typography>
        <Typography variant="subtitle1">{LUX_LOYALTY_TIERS_CONFIG[account.tier].displayName}</Typography>
        {canChangeTier && UPGRADEABLE_TIERS.includes(account.tier) && (
          <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setChangeTierModalOpen(true)}>
            Change Tier
          </Button>
        )}
      </Box>
      <LuxLoyaltyChangeTierModal
        open={changeTierModalOpen}
        onClose={() => setChangeTierModalOpen(false)}
        customerId={account.customerId}
        currentTier={account.tier}
        onSuccess={() => onAccountUpdated?.()}
      />
      <Box>
        <Typography variant="h6">{formattedStatusCreditsName}</Typography>
        <Typography variant="subtitle1">{formatDecimal(account.statusCredits)}</Typography>
      </Box>
      <Box>
        <Typography variant="h6">{formattedCurrencyName}</Typography>
        <Typography variant="subtitle1">{formatDecimal(account.points)}</Typography>
      </Box>
      {canAdjustPoints && (
        <Box>
          <Button variant="outlined" size="small" onClick={() => setAdjustPointsModalOpen(true)}>
            Adjust Points
          </Button>
          <LuxLoyaltyAdjustPointsModal
            open={adjustPointsModalOpen}
            onClose={() => setAdjustPointsModalOpen(false)}
            customerId={account.customerId}
            onSuccess={() => onAccountUpdated?.()}
          />
        </Box>
      )}
      {!!account.statusReviewDate && (
        <Box>
          <Typography variant="h6">Membership Renewal Date</Typography>
          <Typography variant="subtitle1">{formatDateSlashes(account.statusReviewDate)}</Typography>
        </Box>
      )}
      {!!account.totalCreditsForTierRetention && (
        <Box>
          <Typography variant="h6">{formattedStatusCreditsName} Required for Tier Retention</Typography>
          <Typography variant="subtitle1">{account.totalCreditsForTierRetention}</Typography>
        </Box>
      )}
      {!!account.totalCreditsForTierUpgrade && (
        <Box>
          <Typography variant="h6">{formattedStatusCreditsName} Required for Tier Upgrade</Typography>
          <Typography variant="subtitle1">{account.totalCreditsForTierUpgrade}</Typography>
        </Box>
      )}
      <Box>
        <Typography variant="h6">Number of Nights</Typography>
        <Typography variant="subtitle1">{account.numberOfNights}</Typography>
      </Box>
      {!!account.numberOfNightsForTierRetention && (
        <Box>
          <Typography variant="h6">Number of Nights Required for Tier Retention</Typography>
          <Typography variant="subtitle1">{account.numberOfNightsForTierRetention}</Typography>
        </Box>
      )}
      {!!account.numberOfNightsForTierUpgrade && (
        <Box>
          <Typography variant="h6">Number of Nights Required for Tier Upgrade</Typography>
          <Typography variant="subtitle1">{account.numberOfNightsForTierUpgrade}</Typography>
        </Box>
      )}
    </Stack>
  );
}
